import {
    useState,
    useEffect,
    useRef,
    useContext,
    createContext,
    forwardRef,
} from "react"
import { connect } from "react-redux"
import FormControl from "@mui/material/FormControl"
import Tooltip from "@mui/material/Tooltip"
import TextField from "@mui/material/TextField"
import Autocomplete, { autocompleteClasses } from "@mui/material/Autocomplete"
import { labels } from "../../constants/labels"
import useMediaQuery from "@mui/material/useMediaQuery"
import Popper from "@mui/material/Popper"
import { useTheme, styled } from "@mui/material/styles"

import { VariableSizeList } from "react-window"
import * as carOrderActions from "../../redux/carOrder/actions"
import * as userProfileActions from "../../redux/userProfile/actions"

import Typography from "@mui/material/Typography"
import PropTypes from "prop-types"

const StyledFormControl = styled(FormControl)(({ theme }) => ({
    margin: theme.spacing(1),
    minWidth: 100,
}))

const LISTBOX_PADDING = 8 // px

function renderRow(props) {
    const { data, index, style } = props
    const dataSet = data[index]
    const inlineStyle = {
        ...style,
        top: style.top + LISTBOX_PADDING,
    }

    return (
        <Typography component="li" {...dataSet[0]} noWrap style={inlineStyle}>
            <Tooltip
                title={dataSet[1].label + " (" + dataSet[1].orgCode + ")"}
                placement="left"
            >
                <div data-test="railroad-options">{dataSet[1].label}</div>
            </Tooltip>
        </Typography>
    )
}

const OuterElementContext = createContext({})

// eslint-disable-next-line react/display-name
const OuterElementType = forwardRef((props, ref) => {
    const outerProps = useContext(OuterElementContext)
    return <div ref={ref} {...props} {...outerProps} />
})

function useResetCache(data) {
    const ref = useRef(null)
    useEffect(() => {
        if (ref.current != null) {
            ref.current.resetAfterIndex(0, true)
        }
    }, [data])
    return ref
}

// Adapter for react-window
const ListboxComponent = forwardRef(function ListboxComponent(props, ref) {
    const { children, ...other } = props
    const itemData = []
    children.forEach((item) => {
        itemData.push(item)
        itemData.push(...(item.children || []))
    })

    const theme = useTheme()
    const smUp = useMediaQuery(theme.breakpoints.up("sm"), {
        noSsr: true,
    })
    const itemCount = itemData.length
    const itemSize = smUp ? 36 : 48

    const getChildSize = () => {
        return itemSize
    }

    const getHeight = () => {
        if (itemCount > 8) {
            return 8 * itemSize
        }
        return itemData.map(getChildSize).reduce((a, b) => a + b, 0)
    }

    const gridRef = useResetCache(itemCount)

    return (
        <div ref={ref}>
            <OuterElementContext.Provider value={other}>
                <VariableSizeList
                    itemData={itemData}
                    height={getHeight() + 2 * LISTBOX_PADDING}
                    width="100%"
                    ref={gridRef}
                    outerElementType={OuterElementType}
                    innerElementType="ul"
                    itemSize={(index) => getChildSize(itemData[index])}
                    overscanCount={5}
                    itemCount={itemCount}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </div>
    )
})

ListboxComponent.propTypes = {
    children: PropTypes.array,
}

const StyledPopper = styled(Popper)({
    [`& .${autocompleteClasses.listbox}`]: {
        boxSizing: "border-box",
        "& ul": {
            padding: 0,
            margin: 0,
        },
    },
})

function RailroadSearchDropdown({
    allShippersProfile,
    storedShipper,
    storedRailRoad,
    handleRailRoadChanges,
    fetchAllTracks,
    checkEbolWOAccess,
}) {
    const [railroadShipperList, setrailroadShipperList] = useState([])
    const [client, setClient] = useState(
        `${storedShipper} - ${storedRailRoad ? storedRailRoad : ""}`
    )
    const [inputValue, setInputValue] = useState("")

    useEffect(() => {
        setClient(`${storedRailRoad} - ${storedShipper}`)
        // fetchAllTracks()
    }, [fetchAllTracks, storedRailRoad, storedShipper])

    useEffect(() => {
        const railroadShipperList = allShippersProfile.map((shipper, key) => ({
            label:
                shipper.grantors.length > 0
                    ? `${shipper.grantors[0]["orgName"]} - ${shipper.orgName}`
                    : shipper.orgName,
            orgCode: shipper.orgCode,
            key: key,
        }))
        setrailroadShipperList(railroadShipperList)
    }, [allShippersProfile])

    const handleRailroadChange = (event, newValue) => {
        if (newValue !== null) {
            let orgInfo = newValue.label.split(/-(.*)/s)
            if (orgInfo.length) {
                let selectedRailroad = orgInfo[0].trimEnd()
                let selectedShipper = orgInfo[1].trimStart()

                let selectedShipperDetails = allShippersProfile.filter(
                    (org) =>
                        org.orgName === selectedShipper &&
                        org.grantors[0]["orgName"] === selectedRailroad
                )
                setClient(`${orgInfo[0].trimEnd()} - ${selectedShipper}`)
                handleRailRoadChanges(selectedShipperDetails[0])
            } else {
                setClient(orgInfo[0])
                handleRailRoadChanges(orgInfo[0])
            }
        }
    }
    return (
        <StyledFormControl size="small" data-test="dropDown">
            <Autocomplete
                disableListWrap
                value={client}
                onChange={handleRailroadChange}
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue)
                }}
                id="railroad-select"
                data-test="railroad-select-box"
                options={railroadShipperList}
                sx={{
                    width: "250px",
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        sx={{
                            "& .Mui-focused": {
                                color: "grey",
                                fontWeight: "normal",
                            },
                        }}
                    />
                )}
                size="small"
                labelid="railroad-select-outlined-label"
                data-testid="railroad-select-box"
                label="Railroad"
                blurOnSelect
                isOptionEqualToValue={(option, value) => option.label === value}
                renderOption={(props, option) => [props, option]}
                PopperComponent={StyledPopper}
                ListboxComponent={ListboxComponent}
                noOptionsText={`${labels.common.noOrganizationsFound}`}
                clearText=""
                closeText=""
                openText=""
            />
        </StyledFormControl>
    )
}

const mapDispatchToProps = {
    fetchAllTracks: carOrderActions.fetchShipperTracks,
    checkEbolWOAccess: userProfileActions.checkEbolWOAccess,
}
export default connect(null, mapDispatchToProps)(RailroadSearchDropdown)
